import { useChatStore } from '@/store/chatStore'

export type PersonaId = 'product_owner' | 'operations' | 'architect'

export interface PersonaConfig {
  id: PersonaId
  label: string
  shortLabel: string
  description: string
  defaultRoute: (projectId: string) => string
  agentHint: string
  visibleInContextful: boolean
}

const PERSONA_CONFIGS: Record<PersonaId, PersonaConfig> = {
  product_owner: {
    id: 'product_owner',
    label: 'Product Owner',
    shortLabel: 'PO',
    description: 'Plan features, analyze impact, manage priorities',
    defaultRoute: (pid) => `/p/${pid}`,
    agentHint: 'product_owner',
    visibleInContextful: true,
  },
  operations: {
    id: 'operations',
    label: 'Operations',
    shortLabel: 'Ops',
    description: 'Track rollout readiness, incidents, ownership, and operating risks',
    defaultRoute: (pid) => `/p/${pid}`,
    agentHint: 'operations',
    visibleInContextful: true,
  },
  architect: {
    id: 'architect',
    label: 'Forge Architect',
    shortLabel: 'Forge',
    description: 'Open technical repository work in Forge',
    defaultRoute: (pid) => `/p/${pid}/architect`,
    agentHint: 'architect',
    visibleInContextful: false,
  },
}

export const ALL_PERSONAS: PersonaConfig[] = Object.values(PERSONA_CONFIGS).filter(p => p.visibleInContextful)

export function getPersonaConfig(id: PersonaId): PersonaConfig {
  return PERSONA_CONFIGS[id]
}

export function usePersona() {
  const activeProjectId = useChatStore(s => s.activeProjectId)
  const projects = useChatStore(s => s.projects)
  const setProjectPersona = useChatStore(s => s.setProjectPersona)

  const activeProject = projects.find(p => p.id === activeProjectId)
  const activePersona: PersonaId = activeProject?.persona ?? 'product_owner'
  const config = getPersonaConfig(activePersona)

  const switchPersona = (persona: PersonaId) => {
    if (activeProjectId) {
      setProjectPersona(activeProjectId, persona)
    }
  }

  return {
    activePersona,
    config,
    switchPersona,
    isArchitect: activePersona === 'architect',
    isProductOwner: activePersona === 'product_owner',
  }
}
